import React from 'react';
import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { MAX_COMPARED_REPS, SERIES_COLORS } from '../../config/dashboardTheme';
import { DROPDOWN_MAX_WIDTH_PX, DROPDOWN_WIDE_WIDTH_PX } from '../../constants/formLayout';
import { AvailableRep } from './trendTypes';

const COMPARE_PICKER_SX = { minWidth: DROPDOWN_WIDE_WIDTH_PX, maxWidth: DROPDOWN_MAX_WIDTH_PX } as const;

interface RepComparePickerProps {
  reps: AvailableRep[];
  selectedRepIds: string[];
  onChange: (repIds: string[]) => void;
}

/**
 * Lets an admin overlay individual reps on top of the team-average line.
 *
 * Selection is capped at MAX_COMPARED_REPS because that is how many series
 * colours stay tellable apart; once the cap is reached the remaining options
 * are disabled rather than silently ignored.
 *
 * Multiple choice with chips, so it stays a raw MUI Select (ui/Dropdown is
 * single-value) under the same rules: small, label on the border, `wide`.
 */
const RepComparePicker: React.FC<RepComparePickerProps> = ({ reps, selectedRepIds, onChange }) => {
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value;
    const repIds = typeof next === 'string' ? next.split(',') : next;
    onChange(repIds.slice(0, MAX_COMPARED_REPS));
  };

  const emailFor = (repId: string) =>
    reps.find((rep) => rep.userId === repId)?.userEmail ?? repId;

  const isAtCap = selectedRepIds.length >= MAX_COMPARED_REPS;

  // Its value is a row of chips, so unlike a single-value filter it grows with
  // them (up to the cap) rather than stacking them in a fixed width.
  return (
    <FormControl size="small" sx={COMPARE_PICKER_SX} disabled={reps.length === 0}>
      <InputLabel id="rep-compare-label">Compare reps</InputLabel>
      <Select
        multiple
        labelId="rep-compare-label"
        value={selectedRepIds}
        label="Compare reps"
        onChange={handleChange}
        renderValue={(selected) => (
          <div className="flex flex-wrap gap-1">
            {selected.map((repId, index) => (
              <Chip
                key={repId}
                size="small"
                label={emailFor(repId)}
                sx={{
                  height: 20,
                  fontSize: 11,
                  borderColor: SERIES_COLORS[index % SERIES_COLORS.length],
                  color: SERIES_COLORS[index % SERIES_COLORS.length],
                }}
                variant="outlined"
              />
            ))}
          </div>
        )}
      >
        {reps.map((rep) => {
          const isSelected = selectedRepIds.includes(rep.userId);
          return (
            <MenuItem key={rep.userId} value={rep.userId} disabled={!isSelected && isAtCap}>
              <Checkbox size="small" checked={isSelected} />
              <ListItemText
                primary={rep.userEmail}
                secondary={`${rep.sessionCount} session${rep.sessionCount === 1 ? '' : 's'}`}
                primaryTypographyProps={{ fontSize: 13 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default RepComparePicker;
